<script setup lang="ts">
import AuthenticatedLayout from '@/Layouts/AuthenticatedLayout.vue';
import { Head, Link } from '@inertiajs/vue3'
import { library } from "@fortawesome/fontawesome-svg-core"
import {  } from "@fal"
import { faCheckCircle, faTimesCircle } from "@fas"

import PageHeading from "@/Components/Headings/PageHeading.vue"
import { capitalize } from "@/Composables/capitalize"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { inject } from "vue"
import { aikuLocaleStructure } from "@/Composables/useLocaleStructure"
import CountUp from 'vue-countup-v3'
import BackgroundBox from '@/Components/BackgroundBox.vue'
import Icon from '@/Components/Utils/Icon.vue'
import { trans } from 'laravel-vue-i18n'
import Image from '@/Components/Utils/Image.vue'
import { layoutStructure } from '@/Composables/useLayoutStructure'

library.add(faCheckCircle, faTimesCircle )

const props = defineProps<{
    pageHead?: PageHeadingTS
    title: string
}>()
</script>

<template>
    <Head title="Dashboard" />
    <PageHeading :data="pageHead"></PageHeading>
    <!-- <pre>{{ props }}</pre> -->
    <div>
        <!-- <div class="p-6">
            <dl class="grid grid-cols-1 gap-2 lg:gap-5 sm:grid-cols-2 lg:grid-cols-4">
                <Link
                    v-for="(stat, index) in stats"
                    :key="'stat' + index"
                    :href="route(stat.route.name, stat.route.parameters)"
                    :style="{color: stat.color}"
                    class="isolate relative overflow-hidden rounded-lg bg-white hover:bg-gray-50 cursor-pointer border border-gray-200 px-4 py-5 shadow-sm sm:p-6 sm:pb-3"
                >
                    <BackgroundBox class="-z-10 opacity-60 absolute top-0 right-0" />

                    <dt class="truncate text-sm font-medium text-gray-400">
                        {{ stat.label }}
                    </dt>

                    <dd class="mt-1 text-3xl font-semibold tracking-tight flex gap-x-2 items-center">
                        <FontAwesomeIcon :icon='stat.icon' class='text-xl' fixed-width aria-hidden='true' />
                        <CountUp
                            :endVal='stat.value'
                            :duration='1.5'
                            :scrollSpyOnce='true'
                            :options='{
                                formattingFn: (value: number) => locale.number(value)
                            }'
                        />
                    </dd>

                    <div v-if="stat.metas?.length" class="-ml-2 py-2 text-sm text-gray-500 flex gap-x-3 gap-y-0.5 items-center flex-wrap">
                        <component
                            v-for="meta in stat.metas"
                            :is="meta.href?.name ? Link : 'div'"
                            :href="meta.href?.name ? route(meta.href.name, meta.href.parameters) : ''"
                            class="group/sub px-2 flex gap-x-0.5 items-center font-normal"
                            v-tooltip="capitalize(meta.tooltip) || capitalize(meta.icon?.tooltip)"
                        >
                            <FontAwesomeIcon
                                aria-hidden="true"
                                :icon="meta.icon.icon"
                                class="md:opacity-50 group-hover/sub:opacity-100"
                                :class="meta.icon.class"
                                fixed-width
                            />
                            <div class="group-hover/sub:text-gray-700">
                                {{ locale.number(meta.count) }}
                            </div>
                        </component>
                    </div>
                </Link>
            </dl>
        </div> -->

        <!-- Section: Top of the Month -->
        <div v-if="false && totm?.product.value || totm?.department.value || totm?.family.value" class="p-6">
            <div class="text-xl font-semibold py-1 border-b border-gray-200">Top of the month (TotM)</div>
            <dl class="isolate mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:grid-rows-2 h-72">
                <!-- TotM: Product -->
                <div v-if="totm?.product.value" class="row-span-2 example-2 rounded-md">
                    <div class="inner group bg-gray-100 h-full rounded-md px-8 py-8 flex gap-x-4"
                        :style="{
                            background: `color-mix(in srgb, ${layout?.app?.theme[0]} 10%, white)`
                        }"
                    >
                        <div class="aspect-square h-1/2 lg:h-full w-fit flex-shrink-0 rounded-md overflow-hidden">
                            <!-- <img src="https://www.ancientwisdom.biz/wi.php?id=1857494&s=705x705" class="h-full w-auto z-10" /> -->
                            <Image :src="totm?.product.value?.images?.data?.[0]?.source" />
                        </div>

                        <div class="flex flex-col justify-between gap-y-1">
                            <div>
                                <div class="text-indigo-600 text-sm animate-pulse">Product of the month</div>
                                <h3 class="text-xl font-semibold">
                                    {{ totm?.product.value?.name }}
                                </h3>
                                <div class="text-gray-400 text-sm">{{ totm?.product.value?.code || '-' }}</div>
                            </div>
                            <div>
                                <p aria-hidden="true" class="text-gray-500">{{ trans('Sold this month') }}: {{ totm?.product.value?.sold_on_month || '-' }}</p>
                                <p aria-hidden="true" class="text-gray-500">{{ trans('Stock') }}: {{ totm?.product.value?.stock || '-' }}</p>
                                <p aria-hidden="true" class="text-gray-500">{{ trans('Price') }}: {{ totm?.product.value?.price || '-' }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- TotM: Department -->
                <div v-if="totm?.department.value" class="bg-gray-50 border-gray-200 border rounded-md flex items-center p-6">
                    <div class="flex gap-x-2 items-center">
                        <div class="p-3 rounded">
                            <FontAwesomeIcon icon='fal fa-folder-tree' class='text-indigo-500 text-xl' v-tooltip="trans('Department')" fixed-width aria-hidden='true' />
                        </div>
                        <div class="">
                            <div class="text-xl font-medium">{{ totm?.department.value.name }}</div>
                            <div class="flex gap-x-10">
                                <div class="text-gray-500">
                                    <FontAwesomeIcon icon='fal fa-folder' class='text-gray-400' fixed-width aria-hidden='true' />
                                    {{ totm?.department.value.current_families }}
                                </div>
                                <div class="text-gray-500">
                                    <FontAwesomeIcon icon='fal fa-cube' class='text-gray-400' fixed-width aria-hidden='true' />
                                    {{ totm?.department.value.current_products }}
                                </div>
                            </div>
                        </div>
                        
                    </div>
                </div>

                <!-- TotM: Family -->
                <div v-if="totm?.family.value" class="bg-gray-50 border-gray-200 border rounded-md flex items-center p-6">
                    <div class="flex gap-x-2 items-center">
                        <div class="p-3 rounded">
                            <FontAwesomeIcon :icon='totm?.family.icon' class='text-indigo-500 text-xl' v-tooltip="trans('Family')" fixed-width aria-hidden='true' />
                        </div>

                        <div class="">
                            <div class="text-xl font-medium">{{ totm?.family.value.name }}</div>
                            <!-- <div class="flex gap-x-10">
                                <div class="text-gray-500">
                                    <FontAwesomeIcon icon='fal fa-folder' class='text-gray-400' fixed-width aria-hidden='true' />
                                    {{ totm?.department.value.current_families }}
                                </div>
                                <div class="text-gray-500">
                                    <FontAwesomeIcon icon='fal fa-cube' class='text-gray-400' fixed-width aria-hidden='true' />
                                    {{ totm?.department.value.current_products }}
                                </div>
                            </div> -->
                        </div>
                        
                    </div>
                </div>

            </dl>
        </div>
    </div>

    <!-- <AuthenticatedLayout>
        <template #header>
            <h2
                class="text-xl font-semibold leading-tight text-gray-800"
            >
                Dashboard
            </h2>
        </template>

        <div class="py-12">
            <div class="mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div
                    class="overflow-hidden bg-white shadow-sm sm:rounded-lg"
                >
                    <div class="p-6 text-gray-900">
                        You're logged in!
                    </div>
                </div>
            </div>
        </div>
    </AuthenticatedLayout> -->
</template>
